import {
  Button,
  createStyles,
  TextField,
  withStyles,
  type WithStyles,
} from "@material-ui/core";
import { useTranslations } from "adapters/i18n";
import SelectDropdown from "components/SelectDropdown";
import { PrintIcon } from "features/shared/components";
import React, { type ChangeEvent } from "react";
import type { Retreat } from "shared/types";
import { useFilterFormOptions } from "../hooks/useFilterFormOptions";
import type { LotsFilterFormState } from "../lots.types";
import {
  MeasuredWeightFilter,
  ProjectedWeightFilter,
} from "../utils/lots.utils";

type LotsFilterFormProps = {
  farmRetreats: Retreat[];
  filterForm: LotsFilterFormState;
  onChangeFilter: <T extends keyof LotsFilterFormState>(
    field: T,
    value: LotsFilterFormState[T]
  ) => void;
  onPrint: () => void;
} & WithStyles<typeof styles>;

function LotsFilterForm({
  classes,
  farmRetreats,
  filterForm,
  onChangeFilter,
  onPrint,
}: LotsFilterFormProps) {
  const { t } = useTranslations();

  const { measuredWeightOptions, projectedWeightOptions, retreatOptions } =
    useFilterFormOptions({ farmRetreats });

  const handleChangeSearch = (event: ChangeEvent<HTMLInputElement>) => {
    onChangeFilter("searchText", event.target.value);
  };

  const handleChangeRetreat = (value: number) => {
    onChangeFilter("retreatId", value);
  };

  const handleChangeMeasuredWeight = (value: MeasuredWeightFilter) => {
    onChangeFilter("measuredWeight", value);
  };

  const handleChangeProjectedWeight = (value: ProjectedWeightFilter) => {
    onChangeFilter("projectedWeight", value);
  };

  return (
    <div className={classes.root}>
      <SelectDropdown
        className={classes.retreatSelect}
        data-testid="retreat-select"
        label={{ text: t("locales:retreat") }}
        onChange={handleChangeRetreat}
        options={retreatOptions}
        value={filterForm.retreatId}
      />

      <TextField
        autoFocus={true}
        className={classes.textFilter}
        data-testid="text-filter-field"
        label={t("livestock:search-for-category-or-pastures")}
        onChange={handleChangeSearch}
        value={filterForm.searchText}
      />

      <SelectDropdown
        className={classes.weightSelect}
        data-testid="measured-weight-select"
        label={{ text: t("livestock:measured-weight") }}
        onChange={handleChangeMeasuredWeight}
        options={measuredWeightOptions}
        value={filterForm.measuredWeight}
      />
      <SelectDropdown
        className={classes.weightSelect}
        data-testid="projected-weight-select"
        label={{ text: t("livestock:projected-weight") }}
        onChange={handleChangeProjectedWeight}
        options={projectedWeightOptions}
        value={filterForm.projectedWeight}
      />

      <Button
        className={classes.printButton}
        color="primary"
        data-testid="print-page-button"
        onClick={onPrint}
        variant="contained"
      >
        <PrintIcon />
      </Button>
    </div>
  );
}

const styles = () =>
  createStyles({
    root: {
      alignItems: "center",
      display: "flex",
      flexDirection: "row",
      flexGrow: 1,
      flexWrap: "wrap",
      gap: "32px",
      justifyContent: "start",
    },
    retreatSelect: {
      width: "200px !important",
    },
    textFilter: {
      marginTop: "4px",
      width: "300px",
    },
    weightSelect: {
      width: "130px !important",
    },
    printButton: {
      borderRadius: 18,
      boxShadow: "none",
      "@media print": {
        display: "none",
      },
    },
  });

export default withStyles(styles)(LotsFilterForm);
